import { motion } from 'framer-motion';
import { useId, useMemo } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

type PointPosition = { x: number; y: number };

export const SignalChart = ({
  points,
  height = 120
}: {
  points: number[];
  height?: number;
}) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;
  const gradientId = useId().replace(/:/g, '');
  const width = 360;

  const { path, areaPath, finalPointY, positions } = useMemo(() => {
    const steps = Math.max(points.length - 1, 1);
    const toY = (value: number) => height - Math.max(0, Math.min(value, 100)) * (height / 100);

    const coords: PointPosition[] = points.map((value, index) => ({
      x: (index / steps) * width,
      y: toY(value)
    }));

    const chartPath = coords
      .map((coordinate, index) => `${index === 0 ? 'M' : 'L'} ${coordinate.x.toFixed(1)} ${coordinate.y.toFixed(1)}`)
      .join(' ');

    const area = `${chartPath} L ${width} ${height} L 0 ${height} Z`;
    const lastValue = points[points.length - 1] ?? 50;
    return { path: chartPath, areaPath: area, finalPointY: toY(lastValue), positions: coords };
  }, [height, points]);

  return (
    <div className="relative overflow-hidden rounded-lg border border-brand-lime/25 bg-brand-panel-2/85 p-3">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-[8.5rem] w-full" role="img" aria-label="Signal trend chart">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(183,255,106,0.35)" />
            <stop offset="100%" stopColor="rgba(183,255,106,0.02)" />
          </linearGradient>
        </defs>

        {Array.from({ length: 5 }).map((_, index) => {
          const y = (height / 4) * index;
          return <line key={index} x1={0} y1={y} x2={width} y2={y} stroke="rgba(183,255,106,0.08)" strokeWidth="1" />;
        })}

        {Array.from({ length: 12 }).map((_, index) => {
          const x = (width / 11) * index;
          return <line key={`v-${index}`} x1={x} y1={0} x2={x} y2={height} stroke="rgba(88,247,182,0.06)" strokeWidth="1" />;
        })}

        <motion.path
          d={areaPath}
          fill={`url(#${gradientId})`}
          initial={mobileViewport ? { opacity: 1 } : { opacity: 0 }}
          whileInView={mobileViewport ? undefined : { opacity: 1 }}
          viewport={mobileViewport ? undefined : { once: true }}
          animate={reducedMotion ? undefined : { opacity: [0.45, 0.8, 0.45] }}
          transition={{ duration: 4.5, repeat: Infinity }}
        />

        <motion.path
          d={path}
          fill="none"
          stroke="rgba(183,255,106,0.95)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={mobileViewport ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
          animate={mobileViewport ? { pathLength: 1, opacity: 1 } : undefined}
          whileInView={mobileViewport ? undefined : { pathLength: 1, opacity: 1 }}
          viewport={mobileViewport ? undefined : { once: true }}
          transition={{ duration: 1.15, ease: [0.2, 1, 0.32, 1] }}
        />

        {!reducedMotion
          ? positions.map((position, index) => (
              <motion.circle
                key={`point-${index}`}
                cx={position.x}
                cy={position.y}
                r="1.8"
                fill="rgba(217,255,168,0.95)"
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.11 }}
              />
            ))
          : null}

        {!reducedMotion ? (
          <motion.circle
            cx={width}
            cy={finalPointY}
            r="4"
            fill="rgba(217,255,168,1)"
            animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1.3, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
        ) : null}
      </svg>

      <motion.div
        className="pointer-events-none absolute inset-y-0 left-[-30%] w-[26%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.2),transparent)]"
        animate={reducedMotion ? undefined : { x: ['0%', '480%'] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};
